import { PLAYER_MAX_HP, makePlayerStarterInventory } from '../combat/player';
import {
  type Character,
  type CharacterId,
  isAlive,
  validateCharacterName,
} from '../core/character';
import type { Persistence } from '../persistence/Persistence';

/**
 * Boot-screen state machine for the character flow. UI-agnostic so it can be
 * unit-tested without a DOM; `App.ts` is the thin renderer that drives it.
 *
 * Transitions:
 *   - `loading` is the cold state before `init()` resolves.
 *   - `init()` resolves to `create` if no characters exist, or `roster` if any do.
 *   - `createCharacter(name)` returns the new `Character`, leaves the flow in
 *     `roster` so the App can transition into the game.
 *   - `selectCharacter(id)` is the App's signal to enter the game with that
 *     character; the flow returns the resolved character.
 *   - `retire(id)` deletes the character from persistence and re-derives the
 *     screen (`roster` if any remain, `create` otherwise).
 *   - `goToCreate()` is a roster → create transition for the "new character"
 *     button when the roster is non-empty.
 *
 * Slice 1 spawn: every newly-created character starts at the origin `(0, 0)`
 * of the single endless world (ADR-0004; ADR-0018 §1).
 */

export type CharacterFlowScreen =
  | { kind: 'loading' }
  | { kind: 'create' }
  | { kind: 'roster'; characters: readonly Character[] };

export interface CharacterFlowDeps {
  readonly persistence: Pick<
    Persistence,
    'listCharacters' | 'saveCharacter' | 'loadCharacter' | 'deleteCharacter'
  >;
  /** Wall-clock for `createdAt`. Injected for deterministic tests. */
  readonly now: () => number;
  /** New `CharacterId` generator. Injected for deterministic tests. */
  readonly newId: () => CharacterId;
}

export const SPAWN_HEX = { q: 0, r: 0 } as const;
/**
 * Starting HP for a fresh character. Pinned to the shared `PLAYER_MAX_HP`
 * (ADR-0015 has no classes; the V1 `warrior.baseHp` pin retired in HEX-60).
 */
export const SPAWN_HP = PLAYER_MAX_HP;

export class CharacterFlow {
  private screen: CharacterFlowScreen = { kind: 'loading' };

  constructor(private readonly deps: CharacterFlowDeps) {}

  /** Test/glue seam — App.ts queries persistence through here. */
  get persistence(): CharacterFlowDeps['persistence'] {
    return this.deps.persistence;
  }

  getScreen(): CharacterFlowScreen {
    return this.screen;
  }

  async init(): Promise<CharacterFlowScreen> {
    const characters = await this.deps.persistence.listCharacters();
    this.screen = characters.length === 0 ? { kind: 'create' } : { kind: 'roster', characters };
    return this.screen;
  }

  /** Force the screen back to `create` (e.g. from the roster's "New character" button). */
  goToCreate(): CharacterFlowScreen {
    this.screen = { kind: 'create' };
    return this.screen;
  }

  /**
   * Validate the name, persist a new character, and transition to roster.
   * Throws on validation failure so the caller can surface the message.
   */
  async createCharacter(rawName: string): Promise<Character> {
    const validation = validateCharacterName(rawName);
    if (!validation.ok) {
      throw new CharacterFlowValidationError(validation.reason ?? 'invalid name');
    }
    const character: Character = {
      id: this.deps.newId(),
      name: rawName,
      position: { q: SPAWN_HEX.q, r: SPAWN_HEX.r },
      explored: new Set<string>(),
      revealed: new Set<string>(),
      seen: new Set<string>(),
      hp: SPAWN_HP,
      discoveries: 0,
      gold: 0,
      // Fresh characters seed the §5/§19 starter trio and persist it immediately
      // (HEX-63 / ADR-0015 §5): the saveCharacter below writes it to the row.
      inventory: makePlayerStarterInventory(),
      // The 3-die baseline is implicit (ADR-0024 §1) — not stored. Specialist dice
      // gained from level-ups (HEX-117) / chests / caches place-or-decline into the
      // loadout as `die`-cards.
      xp: 0,
      level: 1,
      createdAt: this.deps.now(),
    };
    await this.deps.persistence.saveCharacter(character);
    const characters = await this.deps.persistence.listCharacters();
    this.screen = { kind: 'roster', characters };
    return character;
  }

  /**
   * Look up a character; the App uses this to enter the game with the chosen id.
   * A fallen character (HEX-65) is not selectable — returns `null` so the
   * permadeath sting holds even if a stale id reaches here (the roster already
   * excludes the dead via `listCharacters`).
   */
  async selectCharacter(id: CharacterId): Promise<Character | null> {
    const character = await this.deps.persistence.loadCharacter(id);
    if (character === null || !isAlive(character)) return null;
    return character;
  }

  /**
   * Delete the character and refresh the roster. Returns the screen to render
   * next — `roster` when survivors remain, `create` when none do.
   */
  async retire(id: CharacterId): Promise<CharacterFlowScreen> {
    await this.deps.persistence.deleteCharacter(id);
    const characters = await this.deps.persistence.listCharacters();
    this.screen = characters.length === 0 ? { kind: 'create' } : { kind: 'roster', characters };
    return this.screen;
  }
}

export class CharacterFlowValidationError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = 'CharacterFlowValidationError';
  }
}
